import type { InputRef } from 'antd';
import { Input, InputProps } from 'antd';
import dayjs from 'dayjs';
import { useMemo, useRef } from 'react';

export type DateInputProps = InputProps & {
  value?: string;
};

/**
 * Formats a date string to DD/MM/YYYY format
 * - Adds slashes automatically as user types
 * - Validates the date when complete
 * @param dateString - The raw date string input
 * @returns Formatted date string or empty string if invalid
 */
const formatDate = (dateString: string) => {
  if (!dateString) {
    return dateString;
  }
  let formatted = dateString;
  // Add first slash after day (DD/MM)
  if (formatted.length > 2 && formatted[2] !== '/') {
    formatted = formatted.slice(0, 2) + '/' + formatted.slice(2);
  }

  // Add second slash after month (DD/MM/YYYY)
  if (formatted.length > 5 && formatted[5] !== '/') {
    formatted = formatted.slice(0, 5) + '/' + formatted.slice(5);
  }

  // Validate complete date
  if (formatted.length === 10) {
    const dateObj = dayjs(formatted, 'DD/MM/YYYY');

    if (dateObj.isValid()) {
      formatted = dateObj.format('DD/MM/YYYY');
    } else {
      formatted = '';
    }
  }

  return formatted;
};

/**
 * A specialized date input component that:
 * - Automatically formats dates as DD/MM/YYYY
 * - Validates dates in real-time
 * - Handles cursor position during formatting
 * - Integrates with Ant Design's Input component
 */
export const DateInput = ({
  value = '',
  onChange,
  ...props
}: DateInputProps) => {
  const ref = useRef<InputRef>(null); // Ref updated to InputRef

  // Memoize formatted value to avoid unnecessary recalculations
  const formattedValue = useMemo(() => formatDate(value), [value]);

  /**
   * Handles input changes with proper formatting and cursor positioning
   * @param event - The input change event
   */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { selectionEnd, value: newValue } = event.target;

    // Only proceed if value actually changed
    if (newValue !== value) {
      // Trigger parent's onChange with the new raw value
      onChange?.({ target: { value: newValue } } as any);

      // Calculate new cursor position accounting for added slashes
      const newSelection =
        Math.max(formatDate(newValue).length - newValue.length, 0) +
        (selectionEnd ?? 0);

      // Set cursor position after React updates the DOM
      setTimeout(() => {
        const inputElement = ref.current?.input; // Accessing the underlying HTML input element
        inputElement?.setSelectionRange(newSelection, newSelection);
      });
    }
  };

  return (
    <Input
      {...props}
      ref={ref}
      value={formattedValue}
      onChange={handleChange}
      maxLength={10}
    />
  );
};
